import { Component, ChangeDetectionStrategy, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  GuardedDialogHeaderComponent, DialogEscapeDirective,
  captureSnapshot, snapshotEquals, confirmDiscard, registerUnsavedWork,
  DISCARD_CHANGES_COPY, DISCARD_DRAFT_COPY, type SnapshotShape,
} from '@branduals/dialog-guard';
import { SubmitChordHintComponent } from '../submit-chord-hint/submit-chord-hint.component';
import { SelectChordDirective } from '../../directives/select-chord.directive';
import { isSubmitChord } from '../../utils/keyboard.utils';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSpinnerThird, faTriangleExclamation } from '@fortawesome/pro-duotone-svg-icons';

import { ProvenanceDialogService, ProvenanceDialogCallbacks } from './provenance-dialog.service';
import { LocaleService } from '../../services/locale.service';
import { pluralKey } from '../../utils/plural.utils';
import { GraphQLClient } from '../../graphql/client';
import { MutationError, resolveErrorMessage } from '@branduals/graphql-errors';
import {
  SET_PROVENANCE, BULK_SET_PROVENANCE, SetProvenanceResponse, BulkSetProvenanceResponse,
} from '../../graphql/compliance-operations';
import { CONTENT_ORIGINS, ContentOrigin, Provenance, SetProvenanceInput } from '../../models/compliance.models';

/** The trio the compliance overview counts as AI content (drives field visibility + disclosure default). */
const AI_TRIO: ReadonlySet<ContentOrigin> = new Set(['AI_ASSISTED', 'AI_GENERATED', 'SYNTHESIZED']);
const SOURCE_ORIGINS: ReadonlySet<ContentOrigin> = new Set(['STOCK', 'AGENCY', 'USER_GENERATED']);

/** The typed work a dismiss gesture could lose — the RAW signals, deliberately: text typed into a
 *  field a later origin pick hid is still the member's writing (the payload drops it, but the
 *  guard asks what would be LOST, not what submits). */
interface ProvenanceFace {
  origin: ContentOrigin;
  aiModel: string;
  aiPrompt: string;
  sourceInfo: string;
  disclosureRequired: boolean;
}
const FACE_SHAPE: SnapshotShape<ProvenanceFace> = {
  origin: 'scalar', aiModel: 'scalar', aiPrompt: 'scalar', sourceInfo: 'scalar', disclosureRequired: 'scalar',
};

/**
 * Globally-hosted provenance edit dialog — the ONE write surface for content origin,
 * shared by the detail section, media/drive bulk toolbars, violation fix and the campaign panel.
 * Form-dialog canon: self-contained p-dialog; ALL form signals re-seed on open (the persistent
 * global instance is the highest stale-state risk). Single save is OPTIMISTIC (host patches, this
 * instance reconciles in the background — it outlives the close); bulk is PESSIMISTIC with an
 * honest server-determined count toast (folders/SoftLinks/foreign ids are skipped server-side).
 */
@Component({
  selector: 'app-provenance-dialog',
  standalone: true,
  imports: [
    FormsModule, DialogModule, SelectModule, InputTextModule, TextareaModule,
    ToggleSwitchModule, ButtonModule, TranslateModule, FontAwesomeModule,
    ConfirmDialogModule, GuardedDialogHeaderComponent, DialogEscapeDirective, SubmitChordHintComponent,
    SelectChordDirective,
  ],
  // A LOCAL ConfirmationService + mount: the discard prompt must never render through another
  // surface's confirm chrome (or inherit its leftover keys).
  providers: [ConfirmationService],
  templateUrl: './provenance-dialog.component.html',
  styleUrl: './provenance-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProvenanceDialogComponent {
  protected readonly faSpinnerThird = faSpinnerThird;
  protected readonly faTriangleExclamation = faTriangleExclamation;

  private readonly dialogService = inject(ProvenanceDialogService);
  private readonly graphql = inject(GraphQLClient);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly localeService = inject(LocaleService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly visible = this.dialogService.visible;
  readonly assetIds = this.dialogService.assetIds;

  readonly originOptions = CONTENT_ORIGINS.map(o => ({ value: o, label: `compliance.enums.origin.${o}` }));

  // Form state — re-seeded on every open (single-prefill vs bulk-blank).
  readonly origin = signal<ContentOrigin>('HUMAN');
  readonly aiModel = signal('');
  readonly aiPrompt = signal('');
  readonly sourceInfo = signal('');
  readonly disclosureRequired = signal(false);
  readonly busy = signal(false);

  readonly isBulk = computed(() => this.assetIds().length > 1);
  readonly aiTrio = computed(() => AI_TRIO.has(this.origin()));
  readonly showSource = computed(() => SOURCE_ORIGINS.has(this.origin()));
  /** Editing a verified row: every manual set resets the verification server-side — warn first. */
  readonly showReverifyWarning = computed(
    () => !this.isBulk() && this.dialogService.current()?.disclosureVerifiedAt != null);

  readonly title = computed(() =>
    this.isBulk()
      ? this.translate.instant(pluralKey('compliance.provenanceDialog.titleBulk', this.assetIds().length, this.localeService.activeUILanguage()), { count: this.assetIds().length })
      : this.translate.instant('compliance.provenanceDialog.title'));

  /** The face as it was after the open-time seed; null while closed. */
  private openSnapshot: ProvenanceFace | null = null;

  constructor() {
    // Seed-on-open (re-seed pattern for a persistent global instance), then snapshot the seeded
    // face so an untouched dialog reads clean. Read the trigger signal first, then seed in
    // untracked() — seed() reads other signals (current) that must not become effect producers
    // (playbook §4 effect-tracks-through-call-stack trap).
    effect(() => {
      if (!this.visible()) { this.openSnapshot = null; return; }
      untracked(() => {
        this.seed();
        this.openSnapshot = captureSnapshot(FACE_SHAPE, this.face());
        // A draft the host hands back after a refused save lands AFTER the baseline (ruled 2026-09-16), so the
        // refused values read as typed work and a dismiss asks before losing them a second time.
        const handedBack = this.dialogService.handedBack();
        if (handedBack) this.applyDraft(handedBack);
      });
    });
    // A persistent app-chrome instance: it survives every route change, so a route leave
    // destroys nothing here — only a tab close/reload loses the typed provenance. The voice is
    // pulled at raise time in the same face grain the dismiss prompt uses.
    registerUnsavedWork({
      isDirty: () => this.isDirty(),
      scope: 'unload-only',
    });
    // The shell asks this dialog to close when the Time-travel clock moves. The request lands on the same gate as
    // the X: nothing typed closes at once, an edited field or draft asks first, a save in the air stays.
    this.dialogService.closeRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.guardedClose());
  }

  private face(): ProvenanceFace {
    return {
      origin: this.origin(), aiModel: this.aiModel(), aiPrompt: this.aiPrompt(),
      sourceInfo: this.sourceInfo(), disclosureRequired: this.disclosureRequired(),
    };
  }

  private isDirty(): boolean {
    // An in-flight save counts as COMMITTED (optimistic canon): a tab close mid-round-trip asks
    // nothing, exactly as the dismiss gesture stands down on it.
    return !this.busy() && this.openSnapshot !== null && !snapshotEquals(FACE_SHAPE, this.openSnapshot, this.face());
  }

  /** Every DISMISS gesture (the X, Cancel, Escape) lands here; a save closes on its own path. */
  guardedClose(): void {
    if (this.busy()) return;
    if (!this.isDirty()) { this.dialogService.close(); return; }
    // Bulk opens blank (a draft being composed); single opens prefilled (changes to what exists).
    confirmDiscard(this.confirmation, this.translate,
      this.isBulk() ? DISCARD_DRAFT_COPY : DISCARD_CHANGES_COPY,
      () => this.dialogService.close());
  }

  private seed(): void {
    const current = this.dialogService.current();
    this.origin.set(current?.origin ?? 'HUMAN');
    this.aiModel.set(current?.aiModel ?? '');
    this.aiPrompt.set(current?.aiPrompt ?? '');
    this.sourceInfo.set(current?.sourceInfo ?? '');
    this.disclosureRequired.set(current?.disclosureRequired ?? false);
    this.busy.set(false);
  }

  /** The refused draft laid over the seeded face — the payload's shape, so a hidden field's blank stays blank. */
  private applyDraft(draft: SetProvenanceInput): void {
    this.origin.set(draft.origin);
    this.aiModel.set(draft.aiModel ?? '');
    this.aiPrompt.set(draft.aiPrompt ?? '');
    this.sourceInfo.set(draft.sourceInfo ?? '');
    this.disclosureRequired.set(draft.disclosureRequired);
  }

  /** Origin change re-defaults the disclosure toggle (AI trio → on); the user can override after. */
  onOriginChange(origin: ContentOrigin): void {
    this.origin.set(origin);
    this.disclosureRequired.set(AI_TRIO.has(origin));
  }

  /**
   * Ctrl/⌘+Enter anywhere in the dialog saves — the chord every composer in the app answers to.
   * Bound on the dialog element itself; a press something above already consumed
   * (defaultPrevented) is left alone.
   */
  protected onDialogKeydown(event: KeyboardEvent): void {
    if (event.isComposing || event.defaultPrevented || !isSubmitChord(event)) return;
    if (this.busy()) return;
    event.preventDefault();
    void this.save();
  }

  /**
   * The origin select's own chord path: with its overlay closed the field hands the press over
   * instead of letting PrimeNG open the dropdown — the FIRST chord press saves from there too,
   * through the same gate as the dialog-level chord.
   */
  protected onChordSend(): void {
    if (this.busy()) return;
    void this.save();
  }

  async save(): Promise<void> {
    // Hidden conditional fields never leak into the payload (stale text from a prior origin pick).
    const input: SetProvenanceInput = {
      origin: this.origin(),
      aiModel: this.aiTrio() && this.aiModel().trim() ? this.aiModel().trim() : null,
      aiPrompt: this.aiTrio() && this.aiPrompt().trim() ? this.aiPrompt().trim() : null,
      sourceInfo: this.showSource() && this.sourceInfo().trim() ? this.sourceInfo().trim() : null,
      disclosureRequired: this.disclosureRequired(),
    };
    if (this.isBulk()) {
      await this.saveBulk(input);
    } else {
      this.saveSingle(input);
    }
  }

  /** OPTIMISTIC (constitution default): patch the host now, reconcile in the background. */
  private saveSingle(input: SetProvenanceInput): void {
    const assetId = this.assetIds()[0];
    const callbacks = this.dialogService.callbacks();
    const optimistic: Provenance = {
      origin: input.origin,
      aiModel: input.aiModel,
      aiPrompt: input.aiPrompt,
      sourceInfo: input.sourceInfo,
      disclosureRequired: input.disclosureRequired,
      // Mirrors the server: every manual set resets verification (never show a stale verified line).
      disclosureVerifiedAt: null,
      disclosureVerifiedByUserId: null,
      autoDetected: false,
    };
    callbacks?.onSaved?.(optimistic);
    // The row as it was at open — the baseline a restore reopens over (ruled 2026-09-16).
    const current = this.dialogService.current();
    this.dialogService.close();
    // Fire-and-forget reconcile — this globally-hosted instance outlives the close.
    void this.reconcileSingle(assetId, input, callbacks, current);
  }

  private async reconcileSingle(
    assetId: string, input: SetProvenanceInput, callbacks: ProvenanceDialogCallbacks | null, current: Provenance | null,
  ): Promise<void> {
    try {
      const response = await this.graphql.mutateAsync<SetProvenanceResponse>(
        SET_PROVENANCE, { assetId, input });
      const payload = response.setProvenance;
      if (payload.errors && payload.errors.length > 0) {
        throw new MutationError(payload.errors);
      }
      callbacks?.onSettled?.(payload.provenance ?? null);
    } catch (error) {
      // The host's refusal sentence carries this door (ruled 2026-09-16): the dialog reopens on the same asset, seeded
      // from the row as it was and the refused draft laid over it, with the same host callbacks — nothing typed is lost.
      callbacks?.onError?.(error, () => this.dialogService.open([assetId], current, callbacks ?? {}, input));
    }
  }

  /** PESSIMISTIC (exception 2): the server filters to brand-owned Files — the count is its answer. */
  private async saveBulk(input: SetProvenanceInput): Promise<void> {
    const assetIds = this.assetIds();
    const callbacks = this.dialogService.callbacks();
    this.busy.set(true);
    try {
      const response = await this.graphql.mutateAsync<BulkSetProvenanceResponse>(
        BULK_SET_PROVENANCE, { assetIds, input });
      const payload = response.bulkSetProvenance;
      if (payload.errors && payload.errors.length > 0) {
        throw new MutationError(payload.errors);
      }
      const updated = payload.affectedCount;
      const total = assetIds.length;
      const summary = updated < total
        ? this.translate.instant('compliance.provenanceDialog.bulkPartial', { updated, total })
        : this.translate.instant(pluralKey('compliance.provenanceDialog.bulkDone', updated, this.localeService.activeUILanguage()), { count: updated });
      this.messageService.add({ severity: 'success', summary });
      this.dialogService.close();
      callbacks?.onSettled?.();
    } catch (error) {
      const detail = error instanceof MutationError
        ? error.userErrors.map(u => resolveErrorMessage(u, (k, p) => this.translate.instant(k, p))).join('; ')
        : this.translate.instant('common.errorGeneric');
      this.messageService.add({ severity: 'error', summary: this.translate.instant('common.error'), detail });
    } finally {
      this.busy.set(false);
    }
  }

  onVisibleChange(open: boolean): void {
    if (!open) this.dialogService.close();
  }
}
